import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdvisorCard from '../../components/AdvisorNetwork/AdvisorCard';
import {
  SPECIALISATION_FILTERS,
  getFollowingIds,
  addFollowingId,
} from '../../utils/advisorNetworkConstants';

const PAGE_SIZE = 20;

const AdvisorDiscovery = ({ user }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [advisors, setAdvisors] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [followingIds, setFollowingIds] = useState(() => new Set());
  const [followLoadingId, setFollowLoadingId] = useState(null);

  useEffect(() => {
    if (user?.user_id) {
      setFollowingIds(new Set(getFollowingIds(user.user_id)));
    }
  }, [user?.user_id]);

  const fetchAdvisors = useCallback(
    async ({ nextOffset = 0, append = false, specialisationKey = activeFilter }) => {
      const filter = SPECIALISATION_FILTERS.find((item) => item.key === specialisationKey);
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (filter?.apiValue) {
        params.specialisation = filter.apiValue;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await axios.get('/api/advisor-network/advisors', { params });
        const batch = response.data?.advisors || [];
        setAdvisors((prev) => (append ? [...prev, ...batch] : batch));
        setOffset(nextOffset + batch.length);
        setHasMore(batch.length === PAGE_SIZE);
      } catch (err) {
        console.error('Failed to load advisors:', err);
        setError('Unable to load advisors right now.');
        if (!append) setAdvisors([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [activeFilter],
  );

  useEffect(() => {
    fetchAdvisors({ nextOffset: 0, append: false, specialisationKey: activeFilter });
  }, [activeFilter, fetchAdvisors]);

  const handleFollow = async (advisorId) => {
    if (!user) {
      window.location.href = '/auth';
      return;
    }

    setFollowLoadingId(advisorId);
    try {
      await axios.post(`/api/advisor-network/follow/${advisorId}`);
      setFollowingIds((prev) => new Set(prev).add(advisorId));
      if (user?.user_id) {
        addFollowingId(user.user_id, advisorId);
      }
    } catch (err) {
      console.error('Follow failed:', err);
    } finally {
      setFollowLoadingId(null);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchAdvisors({ nextOffset: offset, append: true, specialisationKey: activeFilter });
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <header className="mb-8 pb-6 border-b border-hairline">
          <h1 className="font-display text-display text-ink mb-2">Find an Advisor</h1>
          <p className="text-body text-ink-muted">
            Verified, credentialed financial advisors. No influencers.
          </p>
        </header>

        <div className="mb-8 -mx-6 px-6 md:mx-0 md:px-0">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {SPECIALISATION_FILTERS.map((filter) => {
              const isActive = activeFilter === filter.key;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setActiveFilter(filter.key)}
                  className={`shrink-0 px-3 py-1.5 rounded-md text-label font-medium border transition-colors duration-300 ${
                    isActive
                      ? 'bg-pine text-bone-raised border-pine'
                      : 'bg-bone text-ink border-pine hover:bg-pine-tint'
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <div className="w-10 h-10 mx-auto mb-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
            <p className="text-body text-ink-muted">Loading advisors…</p>
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-body text-ink-muted mb-4">{error}</p>
            <button
              type="button"
              onClick={() => fetchAdvisors({ nextOffset: 0, append: false })}
              className="btn-secondary"
            >
              Try again
            </button>
          </div>
        ) : advisors.length === 0 ? (
          <div className="py-16 text-center border border-hairline rounded-lg bg-bone-raised">
            <p className="font-display text-h2 text-ink mb-2">No advisors found</p>
            <p className="text-body text-ink-muted">
              Try another specialisation or check back soon.
            </p>
          </div>
        ) : (
          <>
            <div className="hairline-list space-y-0">
              {advisors.map((advisor) => (
                <div key={advisor.advisor_id} className="py-5 first:pt-0">
                  <AdvisorCard
                    advisor={advisor}
                    following={followingIds.has(advisor.advisor_id)}
                    followLoading={followLoadingId === advisor.advisor_id}
                    onFollow={handleFollow}
                  />
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="btn-secondary border-pine text-pine hover:bg-pine-tint min-w-[10rem]"
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}

        {user && (
          <p className="mt-10 text-center text-label text-ink-muted">
            Are you an advisor?{' '}
            <Link to="/advisor-network/compose" className="text-pine hover:underline">
              Post an update
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default AdvisorDiscovery;
